import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Printer, CheckSquare, Square, Archive, FileArchive } from "lucide-react";
import { toast } from "sonner";

const fmt = (v) => (Number(v) || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
const STATUS = { open: "Offen", paid: "Bezahlt", cancelled: "Storniert" };
const statusCls = (s) => s === "paid" ? "bg-green-100 text-green-700" : s === "cancelled" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800";

export default function InvoiceArchive() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState({});
  const [customers, setCustomers] = useState([]);
  const [f, setF] = useState({ date_from: "", date_to: "", customer_id: "all", status: "all" });
  const [printing, setPrinting] = useState(false);
  const [zipping, setZipping] = useState(false);

  const selectedIds = Object.keys(sel).filter((k) => sel[k]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (f.date_from) params.date_from = f.date_from;
      if (f.date_to) params.date_to = f.date_to;
      if (f.customer_id !== "all") params.customer_id = f.customer_id;
      if (f.status !== "all") params.status = f.status;
      const r = await api.get("/invoices/archive", { params });
      setRows(r.data || []);
      setSel({});
    } catch (e) { toast.error("Rechnungen konnten nicht geladen werden"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/customers", { params: { page_size: 1000 } }).then((r) => setCustomers(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [f]);

  const toggle = (id) => setSel((s) => ({ ...s, [id]: !s[id] }));
  const toggleAll = () => {
    const allOn = rows.length > 0 && rows.every((r) => sel[r.id]);
    const next = {};
    if (!allOn) rows.forEach((r) => { next[r.id] = true; });
    setSel(next);
  };

  const printSelected = async () => {
    if (!selectedIds.length) { toast.error("Keine Rechnungen ausgewählt"); return; }
    setPrinting(true);
    try {
      const res = await api.post("/invoices/batch-print.pdf", { delivery_note_ids: selectedIds }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("Sammeldruck fehlgeschlagen"); }
    finally { setPrinting(false); }
  };

  const downloadZip = async () => {
    if (!selectedIds.length) { toast.error("Keine Rechnungen ausgewählt"); return; }
    setZipping(true);
    try {
      const res = await api.post("/delivery-notes/bulk-invoices.zip", { delivery_note_ids: selectedIds }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `rechnungen-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("ZIP-Download fehlgeschlagen"); }
    finally { setZipping(false); }
  };

  const total = rows.reduce((s, r) => s + (r.gross_total || 0), 0);

  return (
    <div data-testid="page-invoice-archive">
      <PageHeader eyebrow="Finanzen" title="Rechnungsarchiv"
        description="Alle Ausgangsrechnungen – filtern, auswählen und als Sammel-PDF drucken." />

      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl overflow-hidden">
        {/* Filter */}
        <div className="p-3 border-b border-[var(--border)] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div><Label className="text-xs">Von</Label>
            <Input type="date" value={f.date_from} data-testid="archive-date-from"
                   onChange={(e) => setF({ ...f, date_from: e.target.value })} /></div>
          <div><Label className="text-xs">Bis</Label>
            <Input type="date" value={f.date_to} data-testid="archive-date-to"
                   onChange={(e) => setF({ ...f, date_to: e.target.value })} /></div>
          <div><Label className="text-xs">Kunde</Label>
            <Select value={f.customer_id} onValueChange={(v) => setF({ ...f, customer_id: v })}>
              <SelectTrigger data-testid="archive-customer"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">Alle Kunden</SelectItem>
                {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div><Label className="text-xs">Status</Label>
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger data-testid="archive-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle</SelectItem>
                <SelectItem value="open">Offen</SelectItem>
                <SelectItem value="paid">Bezahlt</SelectItem>
                <SelectItem value="cancelled">Storniert</SelectItem>
              </SelectContent>
            </Select></div>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[var(--border)] flex-wrap">
          <div className="text-sm text-[var(--text-muted)]">
            {rows.length} Rechnung(en) · <span className="font-semibold text-[var(--text)]">{fmt(total)}</span>
            {selectedIds.length > 0 && <> · {selectedIds.length} ausgewählt</>}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={downloadZip} disabled={zipping || selectedIds.length === 0}
                    data-testid="archive-download-zip" variant="outline"
                    className="rounded-full font-bold">
              <FileArchive size={15} className="mr-1" /> {zipping ? "Erzeuge ZIP…" : `Als ZIP (${selectedIds.length})`}
            </Button>
            <Button onClick={printSelected} disabled={printing || selectedIds.length === 0}
                    data-testid="archive-print-selected"
                    className="rounded-full bg-[var(--pop-pink)] text-white font-bold hover:opacity-90">
              <Printer size={15} className="mr-1" /> {printing ? "Erzeuge PDF…" : `Ausgewählte drucken (${selectedIds.length})`}
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <button onClick={toggleAll} data-testid="archive-select-all" className="hover:opacity-70">
                    {rows.length > 0 && rows.every((r) => sel[r.id])
                      ? <CheckSquare size={18} className="text-[var(--pop-pink)]" />
                      : <Square size={18} className="text-[var(--text-muted)]" />}
                  </button>
                </TableHead>
                <TableHead>Rechnungsnr.</TableHead>
                <TableHead>Datum</TableHead>
                <TableHead>Kunde</TableHead>
                <TableHead className="text-right">Betrag</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-[var(--text-muted)]">Lädt…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-[var(--text-muted)]" data-testid="archive-empty">Keine Rechnungen gefunden.</TableCell></TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.id} data-testid={`archive-row-${r.invoice_number}`}
                          className={sel[r.id] ? "bg-[var(--surface-alt)]" : ""}>
                  <TableCell>
                    <button onClick={() => toggle(r.id)} data-testid={`archive-select-${r.invoice_number}`} className="hover:opacity-70">
                      {sel[r.id] ? <CheckSquare size={18} className="text-[var(--pop-pink)]" /> : <Square size={18} className="text-[var(--text-muted)]" />}
                    </button>
                  </TableCell>
                  <TableCell className="font-mono font-semibold">{r.invoice_number}</TableCell>
                  <TableCell>{r.invoice_date || "—"}</TableCell>
                  <TableCell className="max-w-[220px] truncate">{r.customer_name}</TableCell>
                  <TableCell className="text-right font-semibold">{fmt(r.gross_total)}</TableCell>
                  <TableCell>
                    <span className={`text-xs font-semibold rounded-full px-2.5 py-1 ${statusCls(r.status)}`}>{STATUS[r.status] || r.status}</span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
